/**
 * Product feedback — the client side of the ledger.
 *
 * Shared by every workspace's drawer and by the admin triage view. The
 * submission type is the whole contract: a route TEMPLATE, a view name, an
 * optional facility id, the device class, and the person's four answers.
 * There is no field for a URL, a screenshot or page state, so a caller
 * cannot send one by accident.
 */
import { api } from './api';

export type Workspace = 'PROVIDER' | 'PATIENT_ADVOCATE' | 'UMED' | 'SALES' | 'ADMIN' | 'OTHER';
export type ExternalTool = 'MONDAY' | 'COUNSELEAR' | 'SPREADSHEET' | 'EMAIL' | 'OTHER';
export type FeedbackStatus = 'NEW' | 'TRIAGED' | 'NEEDS_EVIDENCE' | 'ACCEPTED' | 'PARKED' | 'SHIPPED' | 'CLOSED';
export type Category = 'BUG' | 'MISSING_INFORMATION' | 'WORKFLOW_FRICTION' | 'PERFORMANCE' | 'IDEA' | 'OTHER';
export type PromptSource = 'DRAWER' | 'WORKFLOW_PROMPT';
export type DeviceClass = 'PHONE' | 'TABLET' | 'DESKTOP';
export type TriageAction =
  | 'TRIAGE' | 'ASSIGN_OWNER' | 'REQUEST_EVIDENCE' | 'ACCEPT'
  | 'PARK' | 'LINK_IMPLEMENTATION' | 'SHIP' | 'CLOSE';

export const EXTERNAL_TOOLS: ExternalTool[] = ['MONDAY', 'COUNSELEAR', 'SPREADSHEET', 'EMAIL', 'OTHER'];
export const EXTERNAL_TOOL_LABEL: Record<ExternalTool, string> = {
  MONDAY: 'Monday', COUNSELEAR: 'CounselEAR', SPREADSHEET: 'A spreadsheet', EMAIL: 'Email', OTHER: 'Somewhere else',
};

/**
 * What the reporter reads. Outcomes in plain words: no state names, no
 * engineering vocabulary, no PR numbers.
 */
export const STATUS_LABEL: Record<FeedbackStatus, string> = {
  NEW: 'Received',
  TRIAGED: 'Under review',
  NEEDS_EVIDENCE: 'Needs more detail',
  ACCEPTED: 'Accepted · in progress',
  PARKED: 'Parked',
  SHIPPED: 'Shipped',
  CLOSED: 'Closed',
};

export const CATEGORIES: Category[] = ['BUG', 'MISSING_INFORMATION', 'WORKFLOW_FRICTION', 'PERFORMANCE', 'IDEA', 'OTHER'];
export const CATEGORY_LABEL: Record<Category, string> = {
  BUG: 'Bug', MISSING_INFORMATION: 'Missing information', WORKFLOW_FRICTION: 'Workflow friction',
  PERFORMANCE: 'Performance', IDEA: 'Idea', OTHER: 'Other',
};

export const WORKSPACE_LABEL: Record<Workspace, string> = {
  PROVIDER: 'Provider', PATIENT_ADVOCATE: 'Patient advocate', UMED: 'UMED operations', SALES: 'Sales', ADMIN: 'Admin', OTHER: 'Other',
};

export interface FeedbackSubmission {
  workspace: Workspace;
  route_template: string;
  view_name: string;
  facility_id: string | null;
  device_class: DeviceClass;
  viewport_width: number;
  prompt_source: PromptSource;
  task_attempted: string;
  observed_friction: string;
  desired_outcome: string | null;
  left_platform: boolean;
  external_tool: ExternalTool | null;
}

export interface MyFeedbackItem {
  id: string;
  workspace: Workspace;
  view_name: string;
  task_attempted: string;
  status: FeedbackStatus;
  resolution_note: string | null;
  target_release: string | null;
  created_at: string;
  updated_at: string;
  resolved_at: string | null;
}

export interface QueueItem {
  id: string;
  workspace: Workspace;
  roles_held: string[];
  route_template: string;
  view_name: string;
  environment: string;
  build_sha: string | null;
  device_class: DeviceClass | null;
  viewport_width: number | null;
  prompt_source: PromptSource;
  feedback_version: number;
  task_attempted: string;
  observed_friction: string;
  desired_outcome: string | null;
  left_platform: boolean;
  external_tool: ExternalTool | null;
  category: Category | null;
  status: FeedbackStatus;
  resolution_note: string | null;
  target_release: string | null;
  resolved_at: string | null;
  created_at: string;
  updated_at: string;
  facility_id: string | null;
  facility_name: string | null;
  submitted_by_user_id: string;
  submitted_by_name: string;
  submitted_by_email: string;
  assigned_owner_user_id: string | null;
  owner_name: string | null;
}

export interface FeedbackEvent {
  id: string;
  event_type: string;
  from_status: FeedbackStatus | null;
  to_status: FeedbackStatus | null;
  note: string | null;
  detail: Record<string, unknown>;
  created_at: string;
  actor_name: string | null;
}

export interface QueueDetail extends QueueItem {
  events: FeedbackEvent[];
}

export interface QueueCounts {
  new: number; triaged: number; needs_evidence: number; accepted: number;
  parked: number; shipped: number; closed: number; left_platform: number;
}

export interface Owner { id: string; email: string; name: string }

export interface TriageActionBody {
  action: TriageAction;
  note?: string;
  category?: Category;
  owner_user_id?: string;
  target_release?: string;
  link?: string;
}

export interface ApiResult<T> {
  success: boolean;
  data?: T;
  error?: string;
  code?: string;
  message?: string;
}

/** Phone, tablet or desktop — the class, never the exact device. */
export function deviceClass(width: number): DeviceClass {
  if (width < 640) return 'PHONE';
  if (width < 1024) return 'TABLET';
  return 'DESKTOP';
}

/* `api.*` is `apiRequest` bound to a verb, and binding loses the generic. */
export const feedbackApi = {
  submit: async (body: FeedbackSubmission): Promise<ApiResult<{ id: string; status: FeedbackStatus }>> =>
    (await api.post('/feedback', body)) as ApiResult<{ id: string; status: FeedbackStatus }>,

  mine: async (): Promise<ApiResult<MyFeedbackItem[]>> =>
    (await api.get('/feedback/mine')) as ApiResult<MyFeedbackItem[]>,

  queue: async (filter: string, workspace: Workspace | ''): Promise<ApiResult<{ items: QueueItem[]; counts: QueueCounts }>> => {
    const qp = new URLSearchParams({ status: filter });
    if (workspace) qp.set('workspace', workspace);
    return (await api.get(`/feedback/queue?${qp.toString()}`)) as ApiResult<{ items: QueueItem[]; counts: QueueCounts }>;
  },

  get: async (id: string): Promise<ApiResult<QueueDetail>> =>
    (await api.get(`/feedback/${id}`)) as ApiResult<QueueDetail>,

  owners: async (): Promise<ApiResult<Owner[]>> =>
    (await api.get('/feedback/owners')) as ApiResult<Owner[]>,

  act: async (id: string, body: TriageActionBody): Promise<ApiResult<{ id: string; status: FeedbackStatus }>> =>
    (await api.post(`/feedback/${id}/actions`, body)) as ApiResult<{ id: string; status: FeedbackStatus }>,
};
